import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faArrowRight, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import ApiServices from '../../Services/ApiServices';
import SoftGrid from '../../Shared/SoftGrid/SoftGrid';
import DynamicModal from '../../Shared/DynamicModal';
import DeleteAlert from '../../Shared/DeleteAlert';
import { toastHelper } from '../../../Helpers/toastHelper';
import Loader from '../../Shared/Loader';
import MiniLoader from '../../Shared/MiniLoader';
import { motion } from 'framer-motion';
import * as Yup from 'yup';
import { useLanguage } from '../../../contexts/LanguageContext';

const CourseVideoQuizes = ({ courseVideoId: propCourseVideoId, videoTitle = '' }) => {
  const { t } = useLanguage();
  const { courseVideoId: paramCourseVideoId } = useParams();
  const navigate = useNavigate();

  const courseVideoId = propCourseVideoId || paramCourseVideoId;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // Delete Alert State
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!courseVideoId) return;
    setLoading(true);
    try {
      const response = await ApiServices.get(`CourseVideoQuestion/getAll/${courseVideoId}`);
      setData(response.data || []);
    } catch (error) {
      toastHelper.error('تعذر تحميل بيانات الأسئلة');
    } finally {
      setLoading(false);
    }
  }, [courseVideoId]);

  useEffect(() => {
    if (courseVideoId) {
      fetchData();
    }
  }, [courseVideoId, fetchData]);

  const handleAdd = () => {
    setEditId(null);
    setSelectedQuestion(null);
    setIsModalOpen(true);
  };

  const handleEdit = async (row) => {
    setEditId(row.id);
    try {
      const response = await ApiServices.get(`CourseVideoQuestion/${row.id}`);
      setSelectedQuestion(response.data || row);
      setIsModalOpen(true);
    } catch (error) {
      toastHelper.error('تعذر جلب تفاصيل السؤال');
    }
  };

  const handleDelete = (row) => {
    setItemToDelete(row);
    setDeleteAlertOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    try {
      await ApiServices.delete(`CourseVideoQuestion/delete/${itemToDelete.id}`);
      toastHelper.success(t('examDeleteSuccess'));
      fetchData();
    } catch (error) {
      toastHelper.error(error.msg || 'تعذر حذف السؤال');
    } finally {
      setDeleteLoading(false);
      setDeleteAlertOpen(false);
      setItemToDelete(null);
    }
  };

  const formConfig = useMemo(() => {
    const config = [
      {
        name: 'question',
        label: t('examQuestionText'),
        type: 'text',
        required: true,
        defaultValue: selectedQuestion?.question || '',
      },
      {
        name: 'explanation',
        label: t('cvm_explanationLabel') || 'الشرح (توضيح الإجابة)',
        type: 'text',
        required: false,
        defaultValue: selectedQuestion?.explanation || '',
      },
      {
        name: 'timestamp_time',
        label: t('cvm_timestampLabelMinutes') || 'وقت ظهور السؤال',
        type: 'time',
        required: true,
        useSimpleTime: true,
        defaultValue: (() => {
          const totalSec = selectedQuestion?.timestamp_in_seconds || 0;
          const h = Math.floor(totalSec / 3600);
          const m = Math.floor((totalSec % 3600) / 60);
          const s = totalSec % 60;
          return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        })(),
      },
      {
        name: 'score',
        label: t('cvm_scoreLabel') || 'الدرجة',
        type: 'number',
        required: true,
        min: 0,
        defaultValue: selectedQuestion?.score || 1,
      },
    ];

    // Map up to 4 choices to standard fields
    for (let i = 0; i < 4; i++) {
      const choice = selectedQuestion?.choices?.[i];
      config.push({
        name: `choice${i + 1}_text`,
        label: `${t('cvm_choiceLabel') || 'الخيار'} ${i + 1}`,
        type: 'text',
        required: i < 2,
        defaultValue: choice?.text || '',
        validation: Yup.string()
          .nullable()
          .test('unique', t('examQuestionValidationDuplicate') || 'الخيارات يجب أن تكون فريدة', function (value) {
            if (!value || value.trim() === '') return true;
            const { choice1_text, choice2_text, choice3_text, choice4_text } = this.parent;
            const allValues = [choice1_text, choice2_text, choice3_text, choice4_text];
            const count = allValues.filter((v) => v && v.trim() === value.trim()).length;
            return count <= 1;
          })
      });
      if (i < 2) {
         config[config.length - 1].validation = config[config.length - 1].validation.required(t('requiredField') || 'هذا الحقل مطلوب');
      }
      config.push({
        name: `choice${i + 1}_correct`,
        label: (t('cvm_isChoiceCorrect') || 'هل الخيار {n} صحيح؟').replace('{n}', i + 1),
        type: 'select',
        required: i < 2,
        options: [
          { label: t('cvm_correctOption') || 'صحيح', value: 'true' },
          { label: t('cvm_incorrectOption') || 'خاطئ', value: 'false' },
        ],
        defaultValue: choice?.is_correct === true ? 'true' : 'false',
      });
    }

    return config;
  }, [selectedQuestion, t]);

  const handleSubmit = async (formData) => {
    const choices = [];
    for (let i = 1; i <= 4; i++) {
      const text = formData[`choice${i}_text`];
      const isCorrect = formData[`choice${i}_correct`] === 'true';
      if (text && text.trim() !== '') {
        choices.push({
          text: text.trim(),
          is_correct: isCorrect,
        });
      }
    }

    const uniqueTexts = new Set(choices.map(c => c.text));
    if (uniqueTexts.size !== choices.length) {
      toastHelper.error(t('cvq_duplicateOptionError') || 'لا يمكن تكرار نفس الخيار أكثر من مرة.');
      throw new Error('Duplicate options are not allowed');
    }

    // Parse HH:MM:SS time string to total seconds
    const timeValue = formData.timestamp_time || '00:00:00';
    const [hours, minutes, seconds] = timeValue.split(':').map(Number);
    const totalSeconds = (hours * 3600) + (minutes * 60) + (seconds || 0);

    const payload = {
      course_video_id: Number(courseVideoId),
      timestamp_in_seconds: totalSeconds,
      question: formData.question,
      explanation: formData.explanation || '',
      score: Number(formData.score),
      choices: choices,
    };

    try {
      if (editId) {
        payload.id = editId;
        await ApiServices.put(`CourseVideoQuestion/update/${editId}`, payload);
        toastHelper.success(t('cvq_updateQuestionSuccess') || 'تم تحديث السؤال بنجاح');
      } else {
        await ApiServices.post(`CourseVideoQuestion/create`, payload);
        toastHelper.success(t('cvq_addQuestionSuccess') || 'تم إضافة السؤال بنجاح');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      toastHelper.error(error?.msg || t('addEditMozakaraSaveFailed'));
      throw error;
    }
  };

  const displayData = useMemo(() => {
    return data.map(item => {
      const totalSec = item.timestamp_in_seconds || 0;
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      const formatted = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      return {
        ...item,
        timestamp_formatted: formatted,
      };
    });
  }, [data]);

  const columns = [
    { key: 'id', label: 'رقم التعريف' },
    { key: 'question', label: 'السؤال' },
    { key: 'timestamp_formatted', label: 'وقت ظهور السؤال' },
    { key: 'score', label: 'الدرجة' },
  ];

  const actions = [
    {
      label: t('edit'),
      icon: <FontAwesomeIcon icon={faEdit} />,
      onClick: handleEdit,
    },
    {
      label: t('delete'),
      icon: <FontAwesomeIcon icon={faTrash} />,
      onClick: handleDelete,
      className: 'delete',
    },
  ];

  const mainContent = (
    <>
      <SoftGrid
        data={displayData}
        columns={columns}
        actions={actions}
        searchPlaceholder="ابحث في الأسئلة..."
      />

      <DynamicModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editId ? t('examEditQuestion') : t('cvm_addNewQuestionTitle')}
        formConfig={formConfig}
        onSubmit={handleSubmit}
        submitButtonText={t('save')}
      />

      <DeleteAlert
        isOpen={deleteAlertOpen}
        onClose={() => setDeleteAlertOpen(false)}
        onConfirm={confirmDelete}
        title={t('cvq_deleteQuestionConfirmTitle') || "تأكيد حذف السؤال"}
        message={t('cvq_deleteQuestionConfirmMessage') || "هل أنت متأكد من حذف هذا السؤال؟"}
        itemName={itemToDelete?.question}
        loading={deleteLoading}
      />
    </>
  );

  // If rendered embedded (e.g. inside ComponentPopup)
  if (propCourseVideoId) {
    if (loading && !data.length) {
      return (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <MiniLoader size={32} color="#6366f1" />
          <p style={{ marginTop: '1rem', color: '#64748b', fontWeight: 600 }}>{t('cvq_loadingQuestions') || 'جاري تحميل الأسئلة...'}</p>
        </div>
      );
    }
    return mainContent;
  }

  // Standalone Route View
  if (loading && !data.length) {
    return <Loader text={t('cvq_loadingQuestions') || "جاري تحميل الأسئلة..."} />;
  }

  return (
    <div className="cvq-wrapper">
      <div className="cvq-container">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cvq-header glass-panel"
        >
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faArrowRight} />
              <span>العودة للفيديو</span>
            </button>
          </div>
          <div className="header-right">
            <div className="title-section">
              <div className="text-wrapper">
                <h1>{t('cvm_videoQuizQuestions') || 'إدارة أسئلة الفيديو'}</h1>
              </div>
            </div>
            <button className="cvq-btn-primary" onClick={handleAdd}>
              <FontAwesomeIcon icon={faPlus} />
              <span>{t('examAddQuestion')}</span>
            </button>
          </div>
        </motion.div>

        <div className="cvq-content glass-panel" style={{ marginTop: '2rem', padding: '1rem' }}>
          {mainContent}
        </div>
      </div>

      <style jsx>{`
        .cvq-wrapper {
          position: relative;
          min-height: 100vh;
          background-color: #f8fafc;
          overflow: hidden;
          font-family: 'Tajawal', sans-serif;
          padding: 2rem;
        }

        .glass-panel {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
          border-radius: 24px;
        }

        .cvq-container {
          position: relative;
          z-index: 10;
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
        }

        .cvq-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2.5rem;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .text-wrapper h1 {
          margin: 0;
          font-size: 1.75rem;
          font-weight: 800;
          color: #1e293b;
        }

        .back-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          color: #475569;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .back-btn:hover {
          background: #f8fafc;
          color: #1e293b;
          transform: translateY(-2px);
        }

        .cvq-btn-primary {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #4f46e5 0%, #8b5cf6 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cvq-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(79, 70, 229, 0.3);
        }
      `}</style>
    </div>
  );
};

export default CourseVideoQuizes;
